// מסך 2 — כרטיס לקוח: פרטים, מותגים וגרסאות, ספריית חומרים, קמפיינים.
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, Field, Loading, fmtDate, useLoad, useMe } from '../components/ui';
import { CAMPAIGN_STATUS, LANGS, PageHead, Tabs, confirmAction, useAction } from '../components/mgmt/common';
import { LinksEditor, type LinkItem } from '../components/mgmt/LinksEditor';
import { BrandPanel, type BrandRow } from '../components/mgmt/BrandPanel';
import { AssetLibrary } from '../components/mgmt/AssetLibrary';
import { NewCampaignModal } from '../components/mgmt/NewCampaignModal';

interface Client { id: string; name: string; slug: string; status: string; notes: string; links: LinkItem[]; is_demo: boolean; created_at: string; updated_at: string; created_by: string | null; updated_by: string | null }
interface Campaign { id: string; name: string; campaign_key: string; status: string; language: string; start_date: string | null; end_date: string | null; brand_id: string; updated_at: string }
interface ClientFull { client: Client; brands: BrandRow[]; campaigns: Campaign[] }

type Tab = 'brands' | 'assets' | 'campaigns';

export default function ClientPage() {
  const { id } = useParams();
  const me = useMe();
  const canEdit = me.role !== 'viewer';
  const [sp, setSp] = useSearchParams();
  const tab = (sp.get('tab') as Tab) || 'brands';
  const setTab = (t: Tab) => setSp((p) => { p.set('tab', t); return p; }, { replace: true });
  const { data, error, loading, reload, setData } = useLoad<ClientFull>(`/api/clients/${id}`);
  const [editing, setEditing] = useState(false);
  const [newCampaign, setNewCampaign] = useState(false);

  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorBox error={error} />;
  if (!data) return null;
  const c = data.client;
  const brandName = (bid: string) => data.brands.find((b) => b.id === bid)?.name ?? '';

  return (
    <div>
      <PageHead title={c.name} crumbs={<><Link to="/clients">לקוחות</Link> / {c.name}</>}
        sub={<>מזהה <span className="mono">{c.slug}</span> · {c.status === 'active' ? 'פעיל' : 'ארכיון'} · נוצר {fmtDate(c.created_at)} · עודכן {fmtDate(c.updated_at)}</>}>
        {c.is_demo && <span className="badge warn">לקוח הדגמה</span>}
        {c.status === 'archived' && <span className="badge warn">בארכיון</span>}
      </PageHead>

      <section className="card" aria-label="פרטי לקוח" style={{ marginBottom: 12 }}>
        {editing ? <ClientForm client={c} onCancel={() => setEditing(false)} onSaved={(cl) => { setData({ ...data, client: cl }); setEditing(false); }} /> : (
          <div className="row between" style={{ alignItems: 'flex-start' }}>
            <div className="stack grow" style={{ gap: 4 }}>
              {c.notes ? <div style={{ whiteSpace: 'pre-wrap' }}>{c.notes}</div> : <div className="muted small">אין הערות.</div>}
              {c.links.length > 0 && <div className="row">{c.links.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noopener noreferrer">{l.label || l.url}</a>)}</div>}
            </div>
            {canEdit && <div className="row"><button className="btn" onClick={() => setEditing(true)}>עריכת פרטים</button><ArchiveButton client={c} onDone={(cl) => setData({ ...data, client: cl })} /></div>}
          </div>
        )}
      </section>

      <Tabs<Tab> label="אזורי לקוח" value={tab} onChange={setTab} tabs={[
        { id: 'brands', label: `מותגים (${data.brands.length})` },
        { id: 'assets', label: 'חומרים' },
        { id: 'campaigns', label: `קמפיינים (${data.campaigns.length})` },
      ]} />

      <div role="tabpanel">
        {tab === 'brands' && <BrandPanel clientId={c.id} brands={data.brands} onChanged={reload} />}
        {tab === 'assets' && <AssetLibrary clientId={c.id} />}
        {tab === 'campaigns' && (
          <div className="stack">
            <div className="row between">
              <span className="small muted">כל קמפיין נשען על snapshot של גרסת מותג; חומרים מקושרים לכל קמפיין בנפרד.</span>
              {canEdit && <button className="btn primary" onClick={() => setNewCampaign(true)}>קמפיין חדש</button>}
            </div>
            {data.campaigns.length === 0 ? <Empty>אין קמפיינים ללקוח.</Empty> : (
              <table className="table cards-sm">
                <thead><tr><th>קמפיין</th><th>מפתח</th><th>מותג</th><th>שפה</th><th>תאריכים</th><th>סטטוס</th></tr></thead>
                <tbody>
                  {data.campaigns.map((k) => (
                    <tr key={k.id}>
                      <td><Link to={`/campaigns/${k.id}`}><strong>{k.name}</strong></Link><div className="small muted">עודכן {fmtDate(k.updated_at)}</div></td>
                      <td data-l="מפתח"><span className="mono">{k.campaign_key}</span></td>
                      <td data-l="מותג">{brandName(k.brand_id)}</td>
                      <td data-l="שפה">{LANGS[k.language] ?? k.language}</td>
                      <td data-l="תאריכים" className="small">{fmtDay(k.start_date)}{k.end_date ? ` – ${fmtDay(k.end_date)}` : ''}</td>
                      <td data-l="סטטוס"><span className="badge">{CAMPAIGN_STATUS[k.status] ?? k.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      {newCampaign && <NewCampaignModal clientId={c.id} brands={data.brands} onClose={() => setNewCampaign(false)} />}
    </div>
  );
}

export function fmtDay(s: string | null | undefined) { return s ? new Date(s).toLocaleDateString('he-IL') : ''; }

function ClientForm({ client, onCancel, onSaved }: { client: Client; onCancel: () => void; onSaved: (c: Client) => void }) {
  const [name, setName] = useState(client.name);
  const [notes, setNotes] = useState(client.notes);
  const [links, setLinks] = useState<LinkItem[]>(client.links ?? []);
  const { busy, error, run } = useAction();
  useEffect(() => { setName(client.name); }, [client.name]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await run(() => api.patch<Client>(`/api/clients/${client.id}`, { name, notes, links: links.filter((l) => l.url) }), 'פרטי הלקוח נשמרו');
    if (r) onSaved(r);
  };
  return (
    <form className="stack" onSubmit={submit}>
      <Field label="שם *"><input required value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="הערות"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      <LinksEditor value={links} onChange={setLinks} />
      <ErrorBox error={error} />
      <div className="row"><button className="btn primary" disabled={busy || !name.trim()}>{busy ? 'שומר…' : 'שמירה'}</button><button type="button" className="btn" onClick={onCancel}>ביטול</button></div>
    </form>
  );
}

function ArchiveButton({ client, onDone }: { client: Client; onDone: (c: Client) => void }) {
  const { busy, run } = useAction();
  if (client.status === 'archived') {
    return <button className="btn" disabled={busy} onClick={async () => { const r = await run(() => api.patch<Client>(`/api/clients/${client.id}`, { status: 'active' }), 'הלקוח הוחזר לפעילים'); if (r) onDone(r); }}>החזרה לפעילים</button>;
  }
  return (
    <button className="btn danger" disabled={busy} onClick={async () => {
      if (!confirmAction(`להעביר את "${client.name}" לארכיון? הנתונים לא יימחקו וניתן להחזיר אותו לפעילים.`)) return;
      const r = await run(() => api.patch<Client>(`/api/clients/${client.id}`, { status: 'archived' }), 'הלקוח הועבר לארכיון');
      if (r) onDone(r);
    }}>{busy ? '…' : 'העברה לארכיון'}</button>
  );
}
